function preorderDateHasPassed(date) {
    if (!date || date === '') return false;
    const today = new Date().toISOString().slice(0, 10);
    return date < today;
}

function isVariantMetafieldPreorder(variantId) {
    if (typeof deployerPreorderData === 'undefined' || !deployerPreorderData[variantId]) return false;

    const variant = variantData.find(v => v.id == variantId);
    if (!variant || !variant.available) return false;

    const date = typeof deployerPreorderDates !== 'undefined' ? deployerPreorderDates[variantId] : null;
    if (date && date !== '' && preorderDateHasPassed(date)) return false;

    return true;
}

function isBleckmannInStock(variantId) {
    const { stock } = bleckmanStock[variantId] || {};
    return stock > 0;
}

function isInventoryPolicyContinue(variantId) {
    const { inventoryPolicy } = bleckmanStock[variantId] || {};
    return inventoryPolicy === 'continue';
}

function isVariantSellable(variantId) {
    const variant = variantData.find(v => v.id == variantId);
    if (!variant) return false;
    if (!variant.available) return false;

    if (isBleckmannInStock(variantId)) return true;
    if (isInventoryPolicyContinue(variantId)) return true;

    return false;
}

function isVariantStockPreorder(variantId) {
    const variant = variantData.find(v => v.id == variantId);
    if (!variant) return false;
    if (!variant.available) return false;

    if (isBleckmannInStock(variantId)) return false;
    if (isInventoryPolicyContinue(variantId)) return true;

    return false;
}

function isVariantAddToCart(variantId) {
    return isVariantSellable(variantId) && isVariantStockPreorder(variantId) === false;
}

/** Achetable au sens Flytex US : stock Bleckmann > 0, ou vente en rupture autorisée. */
function deployerVariantIsPurchasable(variant) {
    if (!variant) return false;
    return isVariantSellable(variant.id);
}

class DeployerQuantityBreaks extends HTMLElement {
    constructor() {
        super();
        this.container = this.closest('hover-quick-add') || document;
        // Keep broader selectors from file #1
        this.main_product_form =
            this.container.querySelector('.product-form form[action*="/cart/add"]') ||
            this.container.querySelector('.product__page form[action*="/cart/add"]') ||
            this.container.querySelector('.product form[action*="/cart/add"]') ||
            this.container.querySelector('form[action*="/cart/add"]');

        this.main_product_section_id = this.dataset.sectionId;
        this.show_variant_picker = this.getAttribute('data-show-variant-picker') == 'true' ? true : false;
        this.variantData = variantData;
        this.additionalProductsData = additionalProductsData;
        this.additionalTransparentImages = additionalTransparentImages;
        this.templatePreorder = deployerTemplatePreorder;
        this.productPreorder = productLevelPreorder;
        this.isInitializing = true; // Flag pour distinguer l'initialisation des changements ultérieurs
        if (this.main_product_form === null) return console.error('Deployer: No product form found');

        // update when variant changes (works with Broadcast, Prestige, Dawn & co, Impulse)
        if (this.main_product_form.querySelector('input[name="id"]') !== null) {
            this.main_product_form.querySelector('input[name="id"]').addEventListener('change', (event) => {
                this.renderDeployerQuantityBreaks(event.target.value);
            });
        } else {
            if (typeof subscribe === 'function') {
                this.variantChangeSubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
                    this.renderDeployerQuantityBreaks(event.data.variant.id);
                });
            } else {
                this.onVariantChange = (event) => this.renderDeployerQuantityBreaks(event.detail.variant.id);
                document.addEventListener('variant:change', this.onVariantChange);
            }
        }

        // Override theme.js price and button updates - deployer takes priority
        const productContainer = this.closest('.product-section') || this.closest('hover-quick-add');
        if (productContainer) {
            this.productContainer = productContainer;
            this.onVariantPriceChange = () => {
                setTimeout(() => {
                    const selected = this.getSelectedQuantity();
                    if (selected) this.updatePrice(selected);
                }, 50);
            };
            this.onVariantButtonChange = () => {
                setTimeout(() => {
                    this.updateAddButtonState();
                }, 50);
            };
            productContainer.addEventListener('variantPriceChange', this.onVariantPriceChange);
            productContainer.addEventListener('variantChange', this.onVariantButtonChange);
        }
    }

    disconnectedCallback() {
        document.removeEventListener('variant:change', this.onVariantChange);
        this.productContainer?.removeEventListener('variantPriceChange', this.onVariantPriceChange);
        this.productContainer?.removeEventListener('variantChange', this.onVariantButtonChange);
    }

    connectedCallback() {
        this.hoverPriceContainer = this.container.querySelector('hover-product-price');
        this.convertPackCardsCurrency();

        // Clear any existing items inputs on initialization (from file #2)
        this.clearFormInputs();

        if (this.show_variant_picker === false) {
            // Simple mode: initialize inputs and price immediately
            this.updateFormInputs(this.getSelectedQuantity());
            this.updatePrice(this.getSelectedQuantity());
            this.updateAddButtonState();
            this.isInitializing = false; // Réinitialiser le flag après l'initialisation
        } else {
            // Variant picker mode: wait a bit for pickers to init, then compute all prices
            this.updateFormInputs(this.getSelectedQuantity());
            setTimeout(() => {
                this.initializeAllQuantityBreakPrices();
                this.updatePrice(this.getSelectedQuantity());
                this.updateAddButtonState();
                this.isInitializing = false; // Réinitialiser le flag après l'initialisation
            }, 300);
        }

        // update when quantity changes
        if (this.show_variant_picker === false) {
            this.querySelectorAll('.quantity-break').forEach((quantity_break) => {
                quantity_break.addEventListener('click', () => {
                    this.selectQuantity(quantity_break);
                    this.updateFormInputs(quantity_break);
                    this.updateImages(quantity_break);
                });
            });
        } else {
            const variantPickers = this.querySelectorAll('deployer-quantity-break-variant-picker');

            variantPickers.forEach((variantPicker) => {
                variantPicker.addEventListener('variantChange', (event) => {
                    // Sync the picker dataset.id with the selected variant
                    const newVariantId = event.detail.variant.id;
                    variantPicker.dataset.id = newVariantId;
                    // Update form inputs with new variant selection
                    this.updateFormInputs(this.getSelectedQuantity());
                    // Recompute price and button state
                    const selected = this.getSelectedQuantity();
                    if (selected) this.updatePrice(selected);
                    this.updateAddButtonState();
                });
            });

            // Listen to additional product variant changes
            const additionalVariantPickers = this.querySelectorAll('deployer-quantity-break-additional-variant-picker');
            additionalVariantPickers.forEach((variantPicker) => {
                variantPicker.addEventListener('variantChange', (event) => {
                    // Update form inputs and price when additional product variant changes
                    this.updateFormInputs(this.getSelectedQuantity());
                    const selected = this.getSelectedQuantity();
                    if (selected) this.updatePrice(selected);
                    this.updateAddButtonState();
                });
            });

            // update when variant changes in quantity break
            this.querySelectorAll('.quantity-break').forEach((quantity_break) => {
                quantity_break.addEventListener('click', (event) => {
                    this.selectQuantity(quantity_break);
                    if (event.target.type != 'select-one')
                        this.updateImages(quantity_break);
                    setTimeout(() => {
                        this.updateFormInputs(quantity_break);
                        this.updatePrice(quantity_break);
                        this.updateAddButtonState();
                    }, 100);
                });
            });
        }
    }

    updateImages(element) {
        const productContainer = this.closest('.product-section') || this.closest('hover-quick-add');
        const variantId = element.dataset.variantId;
        const variant = this.variantData.filter((variant) => {
            return variant.id == variantId;
        });
        
        // Ne pas déclencher l'événement variantImageChange lors de l'initialisation
        // pour éviter d'afficher l'image de variante au lieu de la première image produit
        if (this.isInitializing) {
            return;
        }

        if (variant[0] && this.dataset.productId && String(variant[0].product_id) !== this.dataset.productId) {
            return;
        }

        productContainer.dispatchEvent(new CustomEvent('variantImageChange', {
            detail: {
                variant: variant[0]
            }
        }));
    }

    renderDeployerQuantityBreaks(variant_id) {
        this.classList.add('loading');
        var variant_param = '?variant=' + variant_id + '&';
        if (variant_id === undefined) variant_param = '?';

        fetch(window.location.pathname + variant_param + 'section_id=' + this.main_product_section_id)
            .then((response) => response.text())
            .then((responseText) => {
                const html = new DOMParser().parseFromString(responseText, 'text/html');

                const deployerQuantityBreaksSource = html.querySelector('deployer-quantity-breaks');
                const deployerQuantityBreaksDestination = this;

                if (deployerQuantityBreaksSource && deployerQuantityBreaksDestination) {
                    deployerQuantityBreaksDestination.innerHTML = deployerQuantityBreaksSource.innerHTML;
                    deployerQuantityBreaksDestination.connectedCallback();

                    // Réattacher les event listeners du cart drawer après modification du DOM
                    const cartDrawer = document.querySelector('deployer-cart-drawer');
                    if (cartDrawer) {
                        // Retirer les marqueurs pour permettre le ré-attachement
                        this.main_product_form.removeAttribute('data-deployer-cart-attached');
                        const addButton = this.main_product_form.querySelector('[name="add"]');
                        if (addButton) {
                            addButton.removeAttribute('data-deployer-click-attached');
                        }
                        // Réattacher les event listeners (qui gérera le type du bouton)
                        cartDrawer.setProductFormCompatibility(cartDrawer);
                    }
                }
            })
            .then(() => {
                this.classList.remove('loading');
            });
    }

    selectQuantity(quantity_break) {
        if (quantity_break.classList.contains('disabled-quantity-break')) return;
        this.resetSelectedQuantity();
        // In picker mode, recompute price when selecting a different bundle
        if (this.show_variant_picker) {
            this.updatePrice(quantity_break);
        }
        quantity_break.classList.add('selected-quantity-break');
    }

    resetSelectedQuantity() {
        this.querySelectorAll('.quantity-break').forEach((quantity_break) => {
            quantity_break.classList.remove('selected-quantity-break');
        });
    }

    updateFormInputs(quantity_break) {
        if (!quantity_break) return;
        // Clear all existing items inputs first (from file #2)
        this.clearFormInputs();

        // Also clear any inputs that might have been created by other scripts
        const allItemsInputs = this.main_product_form.querySelectorAll('input[name*="items"]');
        if (allItemsInputs.length > 0) {
            allItemsInputs.forEach((input) => input.remove());
        }

        const cardPickers = quantity_break.querySelectorAll('deployer-quantity-break-variant-picker');

        if (cardPickers.length === 0) {
            this.enablePageProductInputs();
            const quantityInput = this.main_product_form.querySelector('input[name="quantity"]');
            const quantityValue = quantity_break.getAttribute('data-quantity');

            if (quantityInput) {
                quantityInput.value = quantityValue;
            } else {
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = 'quantity';
                input.value = quantityValue;
                this.main_product_form.appendChild(input);
            }

            // Ajouter le produit supplémentaire si présent
            this.addAdditionalProductInputs(quantity_break, 0);
        } else {
            const variantsPickers = quantity_break.querySelectorAll('deployer-quantity-break-variant-picker');
            if (variantsPickers.length > 0) {
                // Clear existing variant inputs first
                this.clearVariantInputs();
                const leftoverQuantityInput = this.main_product_form.querySelector('input[name="quantity"]');
                if (leftoverQuantityInput) leftoverQuantityInput.remove();

                // Chaque unité est une entrée items[i] explicite : les champs id du
                // formulaire (produit de la page) ne partent pas avec la soumission.
                this.disablePageProductInputs();
                variantsPickers.forEach((variantsPicker, index) => {
                    this.appendHiddenInput(`items[${index}][id]`, variantsPicker.dataset.id);
                    this.appendHiddenInput(`items[${index}][quantity]`, '1');
                });

                // Produit supplémentaire après toutes les unités
                this.addAdditionalProductInputs(quantity_break, variantsPickers.length);
                this.applyPackProperties(quantity_break, variantsPickers);
            }
        }
    }

    // disabled = exclu de la soumission, valeur et écouteurs intacts.
    disablePageProductInputs() {
        this.main_product_form
            .querySelectorAll('select[name="id"], input[name="id"], input[name="product-id"]')
            .forEach((control) => { control.disabled = true; });
    }

    enablePageProductInputs() {
        this.main_product_form
            .querySelectorAll('select[name="id"], input[name="id"], input[name="product-id"]')
            .forEach((control) => { control.disabled = false; });
    }

    appendHiddenInput(name, value) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        this.main_product_form.appendChild(input);
    }

    applyPackProperties(quantity_break, variantsPickers) {
        const packsKey = quantity_break.dataset.hoverPacksKey;
        if (!packsKey) return;
        const packInstance = String(Date.now());
        let hoverCompare = quantity_break.dataset.hoverCompare || '';
        const giftPrice = parseFloat(quantity_break.dataset.hoverGiftPrice) || 0;
        if (hoverCompare && giftPrice > 0) {
            // Comparatif de la ligne fusionnée = comparatif de l'offre + prix barré du cadeau.
            hoverCompare = String(Math.round((parseFloat(hoverCompare) + giftPrice) * 100) / 100);
        }
        variantsPickers.forEach((variantsPicker, index) => {
            const prefix = `items[${index}][properties]`;
            this.appendHiddenInput(`${prefix}[_hover_packs]`, packsKey);
            this.appendHiddenInput(`${prefix}[_hover_packs_title]`, quantity_break.dataset.hoverTitle || '');
            this.appendHiddenInput(`${prefix}[_hover_packs_compare]`, `${hoverCompare}|${quantity_break.dataset.hoverSum || ''}`);
            this.appendHiddenInput(`${prefix}[_hover_packs_instance]`, packInstance);
            // Posé par le Liquid quand le slot est le produit de la page.
            if (variantsPicker.dataset.hoverMaster) {
                this.appendHiddenInput(`${prefix}[_hover_packs_master]`, 'true');
            }
            // Posé par le Liquid sur le slot cadeau : offert, exclu du prix.
            if (variantsPicker.dataset.hoverGift) {
                this.appendHiddenInput(`${prefix}[_hover_packs_gift]`, 'true');
            }
        });
    }

    addAdditionalProductInputs(quantity_break, startIndex) {
        if (quantity_break.dataset.hoverPacksKey) return;

        const additionalProductQuantity = quantity_break.dataset.additionalProductQuantity;

        if (additionalProductQuantity && parseInt(additionalProductQuantity) > 0) {
            // Récupérer la variante sélectionnée du variant picker
            const additionalVariantPicker = quantity_break.querySelector('deployer-quantity-break-additional-variant-picker');
            const additionalProductId = additionalVariantPicker 
                ? additionalVariantPicker.dataset.id 
                : quantity_break.dataset.additionalProductId;

            if (additionalProductId) {
                const idInput = document.createElement('input');
                idInput.type = 'hidden';
                idInput.name = `items[${startIndex}][id]`;
                idInput.value = additionalProductId;
                this.main_product_form.appendChild(idInput);

                const qtyInput = document.createElement('input');
                qtyInput.type = 'hidden';
                qtyInput.name = `items[${startIndex}][quantity]`;
                qtyInput.value = additionalProductQuantity;
                this.main_product_form.appendChild(qtyInput);
            }
        }
    }

    clearFormInputs() {
        const inputs = this.main_product_form.querySelectorAll('input[name^="items"], input[name^="properties[_hover_packs"]');
        inputs.forEach((input) => input.remove());
        // Double-check
        const remaining = this.main_product_form.querySelectorAll('input[name^="items"]');
        if (remaining.length > 0) remaining.forEach((i) => i.remove());
    }

    clearVariantInputs() {
        this.clearFormInputs();
    }

    getSelectedQuantity() {
        return this.querySelector('.selected-quantity-break');
    }

    toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = this.main_product_form;
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        if (!addButton) return;

        if (disable) {
            addButton.setAttribute('disabled', 'disabled');
            if (text) addButtonText.textContent = text;
        } else {
            addButton.removeAttribute('disabled');
            if (text) addButtonText.textContent = text;
        }
        if (!modifyClass) return;
    }

    getPurchasableButtonLabel(selectedQuantityBreak) {
        if (this.templatePreorder) return deployerPreorderLabel;
        if (this.productPreorder) return deployerPreorderLabel;

        // La précommande pilote le libellé, jamais l'activation du bouton.
        const pickers = selectedQuantityBreak.querySelectorAll('deployer-quantity-break-variant-picker');
        const isPreorder = pickers.length > 0
            ? Array.from(pickers).some((fieldset) => fieldset.dataset.preorder === 'true')
            : isVariantStockPreorder(selectedQuantityBreak.dataset.variantId);

        return isPreorder ? deployerPreorderLabel : deployerAddToCartLabel;
    }

    // ===== Added from File #2: smarter button state =====
    updateAddButtonState() {
        const selectedQuantityBreak = this.getSelectedQuantity();
        if (!selectedQuantityBreak) return;

        if (this.show_variant_picker || selectedQuantityBreak.querySelector('deployer-quantity-break-variant-picker')) {
            const variantsSelectedAvailable = Array.from(selectedQuantityBreak.querySelectorAll('deployer-quantity-break-variant-picker'))
                .every((fieldset) => fieldset.dataset.available === 'true');

            if (variantsSelectedAvailable) {
                this.toggleAddButton(false, this.getPurchasableButtonLabel(selectedQuantityBreak), false);
            } else {
                this.toggleAddButton(true, theme.strings?.soldOut || 'Sold out', false);
            }
        } else {
            const variantId = selectedQuantityBreak.dataset.variantId;
            const variant = this.variantData.find(v => v.id == variantId);
            if (variant && (isVariantSellable(variantId) || isVariantStockPreorder(variantId))) {
                this.toggleAddButton(false, this.getPurchasableButtonLabel(selectedQuantityBreak), false);
            } else {
                this.toggleAddButton(true, theme.strings?.soldOut || 'Sold out', false);
            }
        }
    }

    // ===== Replaced pricing with advanced logic from File #2 =====
    updatePrice(quantity_break) {
        const productPriceContainer = this.container.querySelector('[data-product-price].product__price');
        if (!productPriceContainer) return;

        if (this.show_variant_picker === false) {
            // Simple mode: recalculer le prix avec le produit supplémentaire
            const quantityBreakPriceElement = quantity_break.querySelector('.quantity-break_price-container_price');
            const quantityBreakComparePriceElement = quantity_break.querySelector('.quantity-break_price-container_compare-at-price');

            let basePrice = 0;
            let baseComparePrice = 0;

            if (quantityBreakPriceElement) {
                basePrice = parseInt(quantityBreakPriceElement.dataset.priceCents, 10) / 100 || 0;
            }
            if (quantityBreakComparePriceElement) {
                baseComparePrice = parseInt(quantityBreakComparePriceElement.dataset.priceCents, 10) / 100 || 0;
            }

            // Ajouter le produit supplémentaire
            const additionalProductQuantity = quantity_break.dataset.additionalProductQuantity;
            const additionalProductFree = quantity_break.dataset.additionalProductFree === 'true';
            if (additionalProductQuantity && parseInt(additionalProductQuantity) > 0 && !additionalProductFree) {
                // Récupérer la variante sélectionnée du variant picker
                const additionalVariantPicker = quantity_break.querySelector('deployer-quantity-break-additional-variant-picker');
                let price = 0;
                
                if (additionalVariantPicker) {
                    const additionalVariantId = additionalVariantPicker.dataset.id;
                    const quantityBreakIndex = Array.from(this.container.querySelectorAll('.quantity-break')).indexOf(quantity_break) + 1;
                    const additionalVariantData = this.additionalProductsData[quantityBreakIndex.toString()] || [];
                    const additionalVariant = additionalVariantData.find(v => v.id == additionalVariantId);
                    
                    if (additionalVariant) {
                        price = parseFloat(additionalVariant.price) / 100;
                    }
                } else {
                    // Fallback sur le prix par défaut
                    const additionalProductPrice = quantity_break.dataset.additionalProductPrice;
                    if (additionalProductPrice) {
                        price = parseFloat(additionalProductPrice) / 100;
                    }
                }
                
                const quantity = parseInt(additionalProductQuantity);
                basePrice += price * quantity;
                baseComparePrice += price * quantity;
            }

            const priceSpan = productPriceContainer.querySelector('span:not([data-product-compare-price])');
            if (priceSpan) {
                priceSpan.innerHTML = this.formatPrice(basePrice);
            }

            if (baseComparePrice > basePrice) {
                let comparePriceSpan = productPriceContainer.querySelector('span[data-product-compare-price]');
                if (!comparePriceSpan) {
                    comparePriceSpan = document.createElement('span');
                    comparePriceSpan.setAttribute('data-product-compare-price', '');
                    productPriceContainer.appendChild(comparePriceSpan);
                }
                comparePriceSpan.innerHTML = this.formatPrice(baseComparePrice);
            } else {
                const comparePriceSpan = productPriceContainer.querySelector('span[data-product-compare-price]');
                if (comparePriceSpan) comparePriceSpan.remove();
            }
        } else {
            // Variant picker mode: compute total with discounts
            this.updatePriceForSelectedVariants(quantity_break, productPriceContainer);
        }
    }

    initializeAllQuantityBreakPrices() {
        // Initialize price blocks for all quantity breaks to avoid 0€
        this.querySelectorAll('.quantity-break').forEach((quantity_break) => {
            this.updatePriceForSelectedVariants(quantity_break, null);
        });
    }

    updatePriceForSelectedVariants(quantity_break, productPriceContainer) {
        const variantsPickers = quantity_break.querySelectorAll('deployer-quantity-break-variant-picker');
        if (variantsPickers.length === 0) return;

        const isInitialization = !productPriceContainer;

        // Discount params set on the quantity-break element via data attributes
        const discountValue = quantity_break.dataset.discountValue;
        const discountPercentage = quantity_break.dataset.discountPercentage;
        const discountOnTotal = quantity_break.dataset.discountOnTotal === 'true';

        const variantsWithPrices = [];
        variantsPickers.forEach((variantPicker) => {
            const variantId = variantPicker.dataset.id;
            const variant = this.variantData.find(v => v.id == variantId);
            if (variant) {
                // Cadeau : hors des totaux affichés (le pied de carte porte sa valeur).
                if (variantPicker.dataset.hoverGift) return;
                const price = parseFloat(variant.price) / 100; // Shopify prices are in cents
                const comparePrice = !quantity_break.dataset.hoverPacksKey && variant.compare_at_price
                    ? parseFloat(variant.compare_at_price) / 100
                    : price;
                const listPrice = variant.compare_at_price && parseFloat(variant.compare_at_price) / 100 > price
                    ? parseFloat(variant.compare_at_price) / 100
                    : price;
                variantsWithPrices.push({ variant, price, comparePrice, listPrice });
            }
        });

        // Ajouter le produit supplémentaire au calcul du prix
        const additionalProductQuantity = quantity_break.dataset.additionalProductQuantity;
        const additionalProductFree = quantity_break.dataset.additionalProductFree === 'true';
        if (additionalProductQuantity && parseInt(additionalProductQuantity) > 0 && !additionalProductFree) {
            // Récupérer la variante sélectionnée du variant picker
            const additionalVariantPicker = quantity_break.querySelector('deployer-quantity-break-additional-variant-picker');
            if (additionalVariantPicker) {
                const additionalVariantId = additionalVariantPicker.dataset.id;
                const quantityBreakIndex = Array.from(this.container.querySelectorAll('.quantity-break')).indexOf(quantity_break) + 1;
                const additionalVariantData = this.additionalProductsData[quantityBreakIndex.toString()] || [];
                const additionalVariant = additionalVariantData.find(v => v.id == additionalVariantId);
                
                if (additionalVariant) {
                    const price = parseFloat(additionalVariant.price) / 100;
                    const comparePrice = additionalVariant.compare_at_price ? parseFloat(additionalVariant.compare_at_price) / 100 : price;
                    const quantity = parseInt(additionalProductQuantity);
                    for (let i = 0; i < quantity; i++) {
                        variantsWithPrices.push({ variant: additionalVariant, price: price, comparePrice: comparePrice, listPrice: comparePrice });
                    }
                }
            }
        }

        let finalPrice = 0;
        let finalComparePrice = 0;

        if (variantsWithPrices.length > 0) {
            if (discountOnTotal) {
                // Logic 1: discount applied on total
                const totalPrice = variantsWithPrices.reduce((s, v) => s + v.price, 0);
                const totalCompare = variantsWithPrices.reduce((s, v) => s + v.comparePrice, 0);
                finalPrice = totalPrice;
                finalComparePrice = totalCompare;

                if (discountValue && discountValue !== '') {
                    const amount = parseFloat(String(discountValue).replace(',', '.'));
                    finalPrice = Math.max(0, totalPrice - amount);
                } else if (discountPercentage && discountPercentage !== '') {
                    const percent = parseFloat(String(discountPercentage).replace(',', '.'));
                    finalPrice = totalPrice - (totalPrice * percent) / 100;
                }
            } else {
                // Map: for each tier size, how many of the cheapest units receive the discount.
                // Mirrors the SupaEasy "Buy X Get Y" function shape configured in the cart.
                // Add a new entry here if a future tier uses a different BOGO shape.
                const DISCOUNTED_UNITS_BY_TIER_SIZE = {
                    1: 1, // single-item tier — the only unit gets the discount
                    2: 1, // Buy 1 Get 1 at X%
                    3: 1, // Buy 2 Get 1 at X% (X=100 → "1 free")
                    4: 2, // Buy 2 Get 2 at X/2% (X=100 → "Buy 2 Get 2 at 50%")
                };

                const itemsByPriceAsc = [...variantsWithPrices].sort((a, b) => a.price - b.price);
                finalComparePrice = itemsByPriceAsc.reduce((sum, v) => sum + v.comparePrice, 0);

                const tierSize = itemsByPriceAsc.length;
                const discountedUnits = DISCOUNTED_UNITS_BY_TIER_SIZE[tierSize];

                const totalDiscountAmount = (discountValue && discountValue !== '')
                    ? parseFloat(String(discountValue).replace(',', '.'))
                    : 0;
                const totalDiscountPercent = (discountPercentage && discountPercentage !== '')
                    ? parseFloat(String(discountPercentage).replace(',', '.'))
                    : 0;

                const amountOffPerDiscountedUnit = totalDiscountAmount / discountedUnits;
                const percentOffPerDiscountedUnit = totalDiscountPercent / discountedUnits;

                const applyDiscountToUnit = (unitPrice) => {
                    if (amountOffPerDiscountedUnit > 0) return Math.max(0, unitPrice - amountOffPerDiscountedUnit);
                    if (percentOffPerDiscountedUnit > 0) return unitPrice * (1 - percentOffPerDiscountedUnit / 100);
                    return unitPrice;
                };

                finalPrice = itemsByPriceAsc.reduce(
                    (sum, item, index) => sum + (index < discountedUnits ? applyDiscountToUnit(item.price) : item.price),
                    0,
                );
            }
        }

        if (quantity_break.dataset.pairSavingsPct) {
            finalPrice = finalComparePrice - this.pairSavings(quantity_break, finalComparePrice, Math.floor);
        }

        if (finalPrice <= 0) finalPrice = finalComparePrice;
        if (finalComparePrice <= 0) {
            finalComparePrice = variantsWithPrices.reduce((s, v) => s + v.comparePrice, 0);
        }

        if (quantity_break.dataset.hoverDiscountPercent) {
            const percent = Math.min(100, Math.max(1, parseFloat(String(quantity_break.dataset.hoverDiscountPercent).replace(',', '.')) || 1));
            const compareCents = Math.round(finalComparePrice * 100);
            finalComparePrice = compareCents / 100;
            finalPrice = Math.round((compareCents * (100 - percent)) / 100) / 100;
        }
        if (quantity_break.dataset.hoverPrice) {
            finalPrice = parseFloat(String(quantity_break.dataset.hoverPrice).replace(',', '.')) || finalPrice;
        }
        if (quantity_break.dataset.hoverPacksKey) {
            finalComparePrice = variantsWithPrices.reduce((s, v) => s + v.listPrice, 0);
            quantity_break.dataset.hoverSum = Math.round(variantsWithPrices.reduce((s, v) => s + v.price, 0) * 100) / 100;
        }
        if (quantity_break.dataset.hoverCompareFixed === 'true') {
            finalComparePrice = parseFloat(String(quantity_break.dataset.hoverCompare).replace(',', '.')) || finalComparePrice;
        }

        // Update price inside the quantity-break card
        this.updateQuantityBreakPrice(quantity_break, finalPrice, finalComparePrice);

        // Update main product price if not initialization run
        if (!isInitialization && productPriceContainer) {
            this.updatePriceDisplay(productPriceContainer, finalPrice, finalComparePrice, quantity_break);
        }
    }

    updateQuantityBreakPrice(quantity_break, totalPrice, totalComparePrice) {
        const priceElement = quantity_break.querySelector('.quantity-break_price-container_price');
        if (priceElement) {
            priceElement.innerHTML = this.formatPrice(totalPrice);
        }

        const comparePriceElement = quantity_break.querySelector('.quantity-break_price-container_compare-at-price');
        if (comparePriceElement) {
            if (totalComparePrice > totalPrice) {
                comparePriceElement.innerHTML = this.formatPrice(totalComparePrice);
                comparePriceElement.style.display = 'block';
            } else {
                comparePriceElement.style.display = 'none';
            }
        }
        if (quantity_break.dataset.hoverPacksKey && quantity_break.dataset.hoverCompareFixed !== 'true') {
            quantity_break.dataset.hoverCompare = totalComparePrice > totalPrice
                ? String(totalComparePrice)
                : (quantity_break.dataset.hoverGiftPrice ? String(totalPrice) : '');
        }

        const savingsElement = quantity_break.querySelector('.quantity-break_price-container_savings');
        if (savingsElement && savingsElement.dataset.fixedSavings === 'true') {
            savingsElement.style.display = 'block';
        } else if (savingsElement && totalComparePrice > totalPrice) {
            const savings = totalComparePrice - totalPrice;
            const originalSavingsText = savingsElement.dataset.originalText;
            if (originalSavingsText) {
                const updatedSavingsText = originalSavingsText.replace('[savings]', this.formatPrice(savings));
                savingsElement.textContent = updatedSavingsText;
                savingsElement.style.display = 'block';
            }
        } else if (savingsElement) {
            savingsElement.style.display = 'none';
        }

        this.updateSavingsBadge(quantity_break, totalPrice, totalComparePrice);
    }

    updateSavingsBadge(quantity_break, totalPrice, totalComparePrice) {
        const badge = quantity_break.querySelector('[data-savings-badge]');
        if (!badge) return;

        if (badge.matches('hover-campaign-badge')) {
            badge.update(Math.round(totalPrice * 100), Math.round(totalComparePrice * 100));
            return;
        }

        if (badge.dataset.hoverFixedSavings === 'true') {
            badge.style.display = '';
            return;
        }

        const savings = quantity_break.dataset.pairSavingsPct
            ? this.pairSavings(quantity_break, totalComparePrice, Math.ceil)
            : totalComparePrice - totalPrice;
        if (savings <= 0 || totalComparePrice <= 0) {
            badge.style.display = 'none';
            return;
        }

        const prefix = badge.dataset.savingsPrefix || '';
        const formatted = badge.dataset.badgeType === 'percentage'
            ? `${Math.round((savings / totalComparePrice) * 100)}%`
            : this.formatPriceWithoutTrailingZeros(savings);

        badge.textContent = `${prefix} ${formatted}`.trim();
        badge.style.display = '';
    }

    updatePriceDisplay(productPriceContainer, totalPrice, totalComparePrice, quantity_break) {
        let priceSpan = productPriceContainer.querySelector('span:not([data-product-compare-price])');
        if (!priceSpan) {
            priceSpan = document.createElement('span');
            productPriceContainer.appendChild(priceSpan);
        }
        if (!this.hoverPriceContainer) priceSpan.innerHTML = this.formatPrice(totalPrice);
        if (this.hoverPriceContainer) priceSpan.innerHTML = this.formatPriceHover(totalPrice);

        if (totalComparePrice > totalPrice) {
            let comparePriceSpan = productPriceContainer.querySelector('span[data-product-compare-price]');
            if (!comparePriceSpan) {
                comparePriceSpan = document.createElement('span');
                comparePriceSpan.setAttribute('data-product-compare-price', '');
                productPriceContainer.appendChild(comparePriceSpan);
            }
            if (!this.hoverPriceContainer) comparePriceSpan.innerHTML = this.formatPrice(totalComparePrice);
            if (this.hoverPriceContainer) comparePriceSpan.innerHTML = this.formatPriceHover(totalComparePrice);
        } else {
            const comparePriceSpan = productPriceContainer.querySelector('span[data-product-compare-price]');
            if (comparePriceSpan) comparePriceSpan.remove();
        }

        document.dispatchEvent(new CustomEvent('hover:price-update', {
            detail: {
                priceCents: Math.round(totalPrice * 100),
                compareCents: Math.round(totalComparePrice * 100),
                quantityBreak: quantity_break,
            },
        }));
    }

    updateComparePrice(productPriceContainer, quantityBreakComparePriceElement) {
        let comparePriceSpan = productPriceContainer.querySelector('span[data-product-compare-price]');
        if (quantityBreakComparePriceElement) {
            if (comparePriceSpan) {
                comparePriceSpan.innerHTML = quantityBreakComparePriceElement.innerHTML;
            } else {
                comparePriceSpan = document.createElement('span');
                comparePriceSpan.setAttribute('data-product-compare-price', '');
                comparePriceSpan.innerHTML = quantityBreakComparePriceElement.innerHTML;
                productPriceContainer.appendChild(comparePriceSpan);
            }
        } else if (comparePriceSpan) {
            comparePriceSpan.remove();
        }
    }

    convertPackCardsCurrency() {
        const rate = parseFloat(window.Shopify?.currency?.rate) || 1;
        if (rate === 1) return;

        const toLocalCurrency = (amount) => Math.round(parseFloat(amount) * rate * 100) / 100;

        this.querySelectorAll('.quantity-break[data-hover-packs-key]').forEach((card) => {
            const priceElement = card.querySelector('.quantity-break_price-container_price');
            const compareElement = card.querySelector('.quantity-break_price-container_compare-at-price');
            const badge = card.querySelector('[data-savings-badge]');

            let price = parseInt(priceElement?.dataset.priceCents, 10) / 100 || 0;
            let comparePrice = parseInt(compareElement?.dataset.priceCents, 10) / 100 || 0;

            if (card.dataset.hoverPrice) {
                price = toLocalCurrency(card.dataset.hoverPrice);
                card.dataset.hoverPrice = price;
            }
            if (card.dataset.hoverCompareFixed === 'true' && card.dataset.hoverCompare) {
                comparePrice = toLocalCurrency(card.dataset.hoverCompare);
                card.dataset.hoverCompare = comparePrice;
            }

            if (priceElement) {
                priceElement.innerHTML = this.formatPrice(price);
                priceElement.dataset.priceCents = Math.round(price * 100);
            }
            if (compareElement && comparePrice > price) {
                compareElement.innerHTML = this.formatPrice(comparePrice);
                compareElement.dataset.priceCents = Math.round(comparePrice * 100);
            }

            if (!badge?.dataset.hoverSavings) {
                this.updateSavingsBadge(card, price, comparePrice);
            } else if (badge.dataset.badgeType !== 'percentage') {
                const savings = this.formatPriceWithoutTrailingZeros(toLocalCurrency(badge.dataset.hoverSavings));
                badge.textContent = `${badge.dataset.savingsPrefix || ''} ${savings}`.trim();
            }
        });
    }

    pairSavings(quantity_break, comparePrice, rounding) {
        const savingsPercent = parseFloat(quantity_break.dataset.pairSavingsPct);
        const exactCents = Math.round(comparePrice * savingsPercent * 1000) / 1000;
        return rounding(exactCents) / 100;
    }

    formatPrice(price) {
        // Utiliser le format de devise Shopify actuel basé sur le marché
        const shopCurrency = window.Shopify?.currency?.active || 'USD';
        const shopLocale = document.documentElement.lang || 'en-US';

        // Fallback : si le thème utilise déjà Shopify.formatMoney
        if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
            // Convertir le prix en centimes pour formatMoney
            return Shopify.formatMoney(price * 100, theme.moneyFormat || window.theme?.moneyFormat);
        }

        return new Intl.NumberFormat(shopLocale, {
            style: 'currency',
            currency: shopCurrency
        }).format(price);
    }

    formatPriceHover(price) {
        return this.formatPrice(price).replace(/[\s\u00a0\u202f]/g, '');
    }

    formatPriceWithoutTrailingZeros(price) {
        // Équivalent JS du filtre Liquid money_without_trailing_zeros :
        // « $12.00 » devient « $12 », « $12.50 » reste intact. Même
        // devise/locale que le fallback Intl de formatPrice.
        const shopCurrency = window.Shopify?.currency?.active || 'USD';
        const shopLocale = document.documentElement.lang || 'en-US';
        return new Intl.NumberFormat(shopLocale, {
            style: 'currency',
            currency: shopCurrency,
            trailingZeroDisplay: 'stripIfInteger'
        }).format(price);
    }
}

customElements.define('deployer-quantity-breaks', DeployerQuantityBreaks);

class QuantityBreakVariantPicker extends HTMLElement {
    constructor() {
        super();
        this.DeployerQuantityBreaks = this.closest('deployer-quantity-breaks');
        this.inputs = this.querySelectorAll('input[type="radio"]');
        this.selects = this.querySelectorAll('select');
        this.optionsNames = this.querySelectorAll('legend span');
        this.options = [];
        this.currentVariant = this.dataset.id; // as in file #1
        this.variantData = this.dataset.productId
            ? this.DeployerQuantityBreaks.variantData.filter((variant) => String(variant.product_id) === this.dataset.productId)
            : this.DeployerQuantityBreaks.variantData;
        this.isInitializing = true; // Flag pour distinguer l'initialisation des changements ultérieurs
    }

    connectedCallback() {
        this.updateOptions();
        this.updateVariantStatuses();
        this.initializeSelects();

        this.inputs.forEach((input) => {
            input.addEventListener('change', (event) => {
                this.onChange(event);
            });
        });
        this.selects.forEach((select) => {
            select.addEventListener('change', (event) => {
                this.onChange(event);
            });
        });

        // Ensure initial price refresh (from file #2)
        setTimeout(() => {
            const deployerQuantityBreaks = this.closest('deployer-quantity-breaks');
            if (deployerQuantityBreaks) {
                const selectedQuantityBreak = deployerQuantityBreaks.getSelectedQuantity();
                if (selectedQuantityBreak) {
                    deployerQuantityBreaks.updatePrice(selectedQuantityBreak);
                }
            }
        }, 100);
    }

    initializeSelects() {
        // Récupérer la variante par défaut depuis le dataset
        const defaultVariantId = this.dataset.id;
        let defaultVariant = this.variantData.find(v => v.id == defaultVariantId);

        const inStockFirst = this.closest('deployer-quantity-breaks')?.dataset.defaultVariantSelection === 'in-stock-first';
        if (inStockFirst && (!defaultVariant || !isVariantAddToCart(defaultVariant.id))) {
            defaultVariant = this.variantData.find(v => isVariantAddToCart(v.id)) || defaultVariant;
        }

        if (!defaultVariant || !isVariantSellable(defaultVariant.id)) {
            defaultVariant = this.variantData.find(v => isVariantSellable(v.id)) || defaultVariant;
        }
        
        if (defaultVariant) {
            // Mettre à jour tous les selects avec les options de la variante par défaut
            this.selects.forEach((select) => {
                const optionPosition = select.closest('fieldset').dataset.optionPosition;
                if (optionPosition) {
                    const optionValue = defaultVariant[`option${optionPosition}`];
                    if (optionValue) {
                        select.value = optionValue;
                    }
                }
            });
            
            // Mettre à jour tous les inputs radio avec les options de la variante par défaut
            this.inputs.forEach((input) => {
                const fieldset = input.closest('fieldset');
                const optionPosition = fieldset.dataset.optionPosition;
                if (optionPosition) {
                    const optionValue = defaultVariant[`option${optionPosition}`];
                    if (optionValue && input.value === optionValue) {
                        input.checked = true;
                    }
                }
            });
            
            // Déclencher onChange pour mettre à jour l'état
            this.onChange();
        }
    }

    onChange() {
        this.updateOptions();
        this.updateDataId();
        this.updateOptionsNames();
        this.updateVariantStatuses();
        this.updateImages();

        // Ne pas déclencher l'événement variantImageChange lors de l'initialisation
        // pour éviter d'afficher l'image de variante au lieu de la première image produit
        if (this.isInitializing) {
            this.isInitializing = false; // Réinitialiser le flag après la première exécution
            return;
        }

        const productContainer = this.closest('.product-section') || this.closest('hover-quick-add');
        const mainProductId = this.closest('deployer-quantity-breaks')?.dataset.productId;
        if (productContainer && (!this.dataset.productId || this.dataset.productId === mainProductId)) {
            productContainer.dispatchEvent(new CustomEvent('variantImageChange', {
                detail: {
                    variant: this.currentVariant
                }
            }));
        }

        // Trigger price recomputation on parent
        const deployerQuantityBreaks = this.closest('deployer-quantity-breaks');
        if (deployerQuantityBreaks) {
            const selectedQuantityBreak = deployerQuantityBreaks.getSelectedQuantity();
            if (selectedQuantityBreak) {
                deployerQuantityBreaks.updatePrice(selectedQuantityBreak);
                deployerQuantityBreaks.updateAddButtonState();
            }
        }

        this.dispatchEvent(new CustomEvent('variantChange', {
            detail: {
                variant: this.currentVariant
            }
        }));
    }

    updateOptions() {
        this.options = [];
        const fieldsets = Array.from(this.querySelectorAll('fieldset[data-option-position]'));
        fieldsets.sort((a, b) => {
            const posA = parseInt(a.dataset.optionPosition, 10) || 0;
            const posB = parseInt(b.dataset.optionPosition, 10) || 0;
            return posA - posB;
        });
        const maxPosition = fieldsets.reduce((max, fs) => {
            const p = parseInt(fs.dataset.optionPosition, 10) || 0;
            return Math.max(max, p);
        }, 0);
        for (let pos = 1; pos <= maxPosition; pos++) {
            const fieldset = fieldsets.find((fs) => parseInt(fs.dataset.optionPosition, 10) === pos);
            if (!fieldset) continue;
            const selectEl = fieldset.querySelector('select');
            const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');
            if (selectEl) {
                this.options.push(selectEl.value);
            } else if (checkedRadio) {
                this.options.push(checkedRadio.value);
            }
        }
    }

    updateDataId() {
        const matched = this.variantData.find((variant) => {
            if (!variant.options || variant.options.length !== this.options.length) return false;
            return !variant.options
                .map((option, index) => {
                    return this.options[index] === option;
                })
                .includes(false);
        });
        if (matched) {
            this.currentVariant = matched;
        } else {
            const fallback = this.variantData.find((v) => String(v.id) === String(this.dataset.id));
            if (fallback) this.currentVariant = fallback;
        }
        if (!this.currentVariant) return;

        this.dataset.id = String(this.currentVariant.id);

        this.dataset.available = deployerVariantIsPurchasable(this.currentVariant) ? 'true' : 'false';

        const preorder = isVariantStockPreorder(this.currentVariant.id);
        this.dataset.preorder = preorder ? 'true' : 'false';
    }

    updateVariantStatuses() {
        const selectedOptions = this.querySelectorAll(':checked');
        const inputWrappers = [...this.querySelectorAll('fieldset')];
        let selectedOptionOneVariants = null;
        let selectedOptionTwoVariants = null;
        let selectedOptionThreeVariants = null;
        if (selectedOptions[0]) {
            selectedOptionOneVariants = this.variantData.filter((variant) => selectedOptions[0].value === variant.options[0]);
        }
        if (selectedOptions[1]) {
            selectedOptionTwoVariants = this.variantData.filter((variant) => selectedOptions[1].value === variant.options[1]);
        }
        if (selectedOptions[2]) {
            selectedOptionThreeVariants = this.variantData.filter((variant) => selectedOptions[2].value === variant.options[2]);
        }
        inputWrappers.forEach((option, index) => {
            if (selectedOptions.length <= 1) return;
            if (index === 0) {
                const optionInputs = [...option.querySelectorAll('input[type="radio"], option')];
                const previousOptionSelected = inputWrappers[index + 1].querySelector(':checked').value;
                const availableOptionInputsValue = selectedOptionTwoVariants.filter((variant) => deployerVariantIsPurchasable(variant) && variant.options[index + 1] === previousOptionSelected).map((variantOption) => variantOption.options[index]);
                this.setInputAvailability(optionInputs, availableOptionInputsValue);
            } else {
                const optionInputs = [...option.querySelectorAll('input[type="radio"], option')];
                const previousOptionSelected = inputWrappers[index - 1].querySelector(':checked').value;
                const availableOptionInputsValue = selectedOptionOneVariants.filter((variant) => deployerVariantIsPurchasable(variant) && variant.options[index - 1] === previousOptionSelected).map((variantOption) => variantOption.options[index]);
                this.setInputAvailability(optionInputs, availableOptionInputsValue);
            }
        });
    }

    setInputAvailability(elementList, availableValuesList) {
        elementList.forEach((element) => {
            const value = element.getAttribute('value');
            const availableElement = availableValuesList.includes(value);

            if (element.tagName === 'INPUT') {
                if (availableElement) {
                    element.classList.remove('disabled');
                } else {
                    element.classList.add('disabled');
                }
            } else if (element.tagName === 'OPTION') {
                if (availableElement) {
                    //element.disabled = false;
                    element.classList.remove('disabled');
                } else {
                    //element.disabled = true;
                    element.classList.add('disabled');
                }
            }
        });
    }

    updateImages() {
        const variant = this.variantData.filter((variant) => {
            return variant.id === this.currentVariant.id;
        });

        const transparentImage = this.getTransparentImage(this.currentVariant.id);

        if (transparentImage) {
            this.setImage(transparentImage);
        } else if (variant[0]?.featured_image?.src) {
            this.setImage(variant[0].featured_image.src);
        }
    }

    /**
     * Set image
     */
    setImage(imageUrl) {
        // Slot cadeau : sa vignette vit dans le pied de carte, pas dans la mosaïque.
        if (this.dataset.hoverGift) {
            const card = this.closest('.quantity-break');
            const giftImage = card && card.querySelector('.hv-quantity-break-footer__gift-image img');
            if (giftImage) giftImage.src = imageUrl;
            return;
        }

        const quantityBreakMainContent = this.closest('.quantity-break_main-content');
        const images = quantityBreakMainContent.querySelectorAll('img');
        const index = Number.parseInt(this.dataset.index) - 1;
        const image = images[index];

        // Slot sans vignette dans la mosaïque : rien à mettre à jour.
        if (!image) return;

        image.src = imageUrl;
    }

    /**
     * Get the transparent image from the variant id
     * @returns string|null
     */
    getTransparentImage(variantId) {
        const transparentImage = transparentImages.find((variant) => variant.id === variantId);
        return transparentImage?.transparentImage;
    }

    updateOptionsNames() {
        this.optionsNames.forEach((optionName, index) => {
            optionName.innerText = this.options[index];
        });
    }
}
customElements.define('deployer-quantity-break-variant-picker', QuantityBreakVariantPicker);

class QuantityBreakAdditionalVariantPicker extends HTMLElement {
    constructor() {
        super();
        this.DeployerQuantityBreaks = this.closest('deployer-quantity-breaks');
        this.inputs = this.querySelectorAll('input[type="radio"]');
        this.selects = this.querySelectorAll('select');
        this.optionsNames = this.querySelectorAll('legend span');
        this.options = [];
        this.currentVariant = this.dataset.id;
        
        // Trouver l'index de la quantity break
        const quantityBreak = this.closest('.quantity-break');
        const quantityBreakIndex = Array.from(this.DeployerQuantityBreaks.container.querySelectorAll('.quantity-break')).indexOf(quantityBreak) + 1;
        this.variantData = this.DeployerQuantityBreaks.additionalProductsData[quantityBreakIndex.toString()] || [];
    }

    connectedCallback() {
        this.updateOptions();
        this.updateVariantStatuses();
        this.initializeSelects();

        this.inputs.forEach((input) => {
            input.addEventListener('change', (event) => {
                this.onChange(event);
            });
        });
        this.selects.forEach((select) => {
            select.addEventListener('change', (event) => {
                this.onChange(event);
            });
        });
    }

    initializeSelects() {
        const defaultVariantId = this.dataset.id;
        let defaultVariant = this.variantData.find(v => v.id == defaultVariantId);

        if (!defaultVariant || !isVariantSellable(defaultVariant.id)) {
            defaultVariant = this.variantData.find(v => isVariantSellable(v.id)) || defaultVariant;
        }
        
        if (defaultVariant) {
            this.selects.forEach((select) => {
                const optionPosition = select.closest('fieldset').dataset.optionPosition;
                if (optionPosition) {
                    const optionValue = defaultVariant[`option${optionPosition}`];
                    if (optionValue) {
                        select.value = optionValue;
                    }
                }
            });
            
            this.inputs.forEach((input) => {
                const fieldset = input.closest('fieldset');
                const optionPosition = fieldset.dataset.optionPosition;
                if (optionPosition) {
                    const optionValue = defaultVariant[`option${optionPosition}`];
                    if (optionValue && input.value === optionValue) {
                        input.checked = true;
                    }
                }
            });
            
            this.onChange();
        }
    }

    onChange() {
        this.updateOptions();
        this.updateDataId();
        this.updateOptionsNames();
        this.updateVariantStatuses();
        this.updateImages();

        // Trigger price recomputation on parent
        const deployerQuantityBreaks = this.closest('deployer-quantity-breaks');
        if (deployerQuantityBreaks) {
            const selectedQuantityBreak = deployerQuantityBreaks.getSelectedQuantity();
            if (selectedQuantityBreak) {
                deployerQuantityBreaks.updatePrice(selectedQuantityBreak);
                deployerQuantityBreaks.updateAddButtonState();
                
                // Update form inputs with new variant
                deployerQuantityBreaks.updateFormInputs(selectedQuantityBreak);
            }
        }

        this.dispatchEvent(new CustomEvent('variantChange', {
            detail: {
                variant: this.currentVariant
            }
        }));
    }

    updateImages() {
        if (!this.currentVariant) return;
        
        const variant = this.variantData.find((v) => v.id === this.currentVariant.id);
        if (!variant) return;

        let imageUrl = null;

        // Check for transparent image metafield first
        const transparentImage = this.getTransparentImage(this.currentVariant.id);
        
        if (transparentImage) {
            imageUrl = transparentImage;
        } else if (variant.featured_image?.src) {
            imageUrl = variant.featured_image.src;
        }

        if (imageUrl) {
            this.setAdditionalProductImage(imageUrl);
        }
    }

    getTransparentImage(variantId) {
        const quantityBreak = this.closest('.quantity-break');
        const quantityBreakIndex = Array.from(this.DeployerQuantityBreaks.container.querySelectorAll('.quantity-break')).indexOf(quantityBreak) + 1;
        const transparentImagesForProduct = this.DeployerQuantityBreaks.additionalTransparentImages[quantityBreakIndex.toString()] || [];
        const transparentImage = transparentImagesForProduct.find((variant) => variant.id === variantId);
        return transparentImage?.transparentImage;
    }

    setAdditionalProductImage(imageUrl) {
        const quantityBreakMainContent = this.closest('.quantity-break_main-content');
        if (!quantityBreakMainContent) return;

        // Find all additional product images (those with the data attribute)
        const additionalImages = quantityBreakMainContent.querySelectorAll('img[data-additional-product="true"]');
        
        // Update all additional product images with the new variant image
        additionalImages.forEach((img) => {
            img.src = imageUrl;
        });
    }

    updateOptions() {
        this.options = [];
        const fieldsets = Array.from(this.querySelectorAll('fieldset[data-option-position]'));
        fieldsets.sort((a, b) => {
            const posA = parseInt(a.dataset.optionPosition, 10) || 0;
            const posB = parseInt(b.dataset.optionPosition, 10) || 0;
            return posA - posB;
        });
        const maxPosition = fieldsets.reduce((max, fs) => {
            const p = parseInt(fs.dataset.optionPosition, 10) || 0;
            return Math.max(max, p);
        }, 0);
        for (let pos = 1; pos <= maxPosition; pos++) {
            const fieldset = fieldsets.find((fs) => parseInt(fs.dataset.optionPosition, 10) === pos);
            if (!fieldset) continue;
            const selectEl = fieldset.querySelector('select');
            const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');
            if (selectEl) {
                this.options.push(selectEl.value);
            } else if (checkedRadio) {
                this.options.push(checkedRadio.value);
            }
        }
    }

    updateDataId() {
        const matched = this.variantData.find((variant) => {
            if (!variant.options || variant.options.length !== this.options.length) return false;
            return !variant.options
                .map((option, index) => {
                    return this.options[index] === option;
                })
                .includes(false);
        });
        if (matched) {
            this.currentVariant = matched;
        } else {
            const fallback = this.variantData.find((v) => String(v.id) === String(this.dataset.id));
            if (fallback) this.currentVariant = fallback;
        }
        if (!this.currentVariant) return;

        this.dataset.id = String(this.currentVariant.id);
        this.dataset.available = deployerVariantIsPurchasable(this.currentVariant) ? 'true' : 'false';
    }

    updateVariantStatuses() {
        const selectedOptions = this.querySelectorAll(':checked');
        const inputWrappers = [...this.querySelectorAll('fieldset')];
        let selectedOptionOneVariants = null;
        let selectedOptionTwoVariants = null;
        let selectedOptionThreeVariants = null;
        if (selectedOptions[0]) {
            selectedOptionOneVariants = this.variantData.filter((variant) => selectedOptions[0].value === variant.options[0]);
        }
        if (selectedOptions[1]) {
            selectedOptionTwoVariants = this.variantData.filter((variant) => selectedOptions[1].value === variant.options[1]);
        }
        if (selectedOptions[2]) {
            selectedOptionThreeVariants = this.variantData.filter((variant) => selectedOptions[2].value === variant.options[2]);
        }
        inputWrappers.forEach((option, index) => {
            if (selectedOptions.length <= 1) return;
            if (index === 0) {
                const optionInputs = [...option.querySelectorAll('input[type="radio"], option')];
                const previousOptionSelected = inputWrappers[index + 1].querySelector(':checked').value;
                const availableOptionInputsValue = selectedOptionTwoVariants.filter((variant) => deployerVariantIsPurchasable(variant) && variant.options[index + 1] === previousOptionSelected).map((variantOption) => variantOption.options[index]);
                this.setInputAvailability(optionInputs, availableOptionInputsValue);
            } else {
                const optionInputs = [...option.querySelectorAll('input[type="radio"], option')];
                const previousOptionSelected = inputWrappers[index - 1].querySelector(':checked').value;
                const availableOptionInputsValue = selectedOptionOneVariants.filter((variant) => deployerVariantIsPurchasable(variant) && variant.options[index - 1] === previousOptionSelected).map((variantOption) => variantOption.options[index]);
                this.setInputAvailability(optionInputs, availableOptionInputsValue);
            }
        });
    }

    setInputAvailability(elementList, availableValuesList) {
        elementList.forEach((element) => {
            const value = element.getAttribute('value');
            const availableElement = availableValuesList.includes(value);

            if (element.tagName === 'INPUT') {
                if (availableElement) {
                    element.classList.remove('disabled');
                } else {
                    element.classList.add('disabled');
                }
            } else if (element.tagName === 'OPTION') {
                if (availableElement) {
                    //element.disabled = false;
                    element.classList.remove('disabled');
                } else {
                    //element.disabled = true;
                    element.classList.add('disabled');
                }
            }
        });
    }

    updateOptionsNames() {
        this.optionsNames.forEach((optionName, index) => {
            optionName.innerText = this.options[index];
        });
    }
}
customElements.define('deployer-quantity-break-additional-variant-picker', QuantityBreakAdditionalVariantPicker);
